import { ValueLabelPair } from 'data/types';
import {
  EWarrant, EWarrantDetailPatch, EWarrantDetailResponse, EWarrantDocument, EWarrantGridRowData,
  EWarrantListItem, EWarrantRawDocument, EWarrantStatus
} from './eWarrant.types';

export const NOT_AVAILABLE = '-';

/**
 * Xcipio AUTHSTATUS for an authorisation update (an extension, a revised set of documents)
 * to an e-warrant whose warrant Xcipio has already provisioned. Approving it has to revise
 * that warrant's dates rather than generate a new one — see EWarrantView's submitDecision,
 * which reads it off EWarrant.rawStatus since adaptAuthStatus below maps it the same as a
 * first-time AwaitingApproval.
 */
export const AWAITING_UPDATE_APPROVAL_STATUS = 'AwaitingUpdateApproval';

/**
 * Xcipio AUTHSTATUS values that correspond to a decision state the portal knows.
 * Anything outside this table (Expired, Cancelled, ...) is shown as Xcipio reported
 * it rather than being forced into one of the decision states the portal knows.
 *
 * The mapping is many to one: more than one AUTHSTATUS can stand for the same decision
 * state, and not every key here is one the portal itself writes.
 */
const AUTH_STATUS_TO_EWARRANT_STATUS: { [authStatus: string]: EWarrantStatus } = {
  AwaitingApproval: EWarrantStatus.Pending,
  // An authorisation waiting for an update to be approved (an extension, a revised set of
  // documents) is undecided in exactly the way one waiting for its initial approval is, and
  // the operator decides it the same way — so it is Pending as far as the portal goes.
  [AWAITING_UPDATE_APPROVAL_STATUS]: EWarrantStatus.Pending,
  Approved: EWarrantStatus.Approved,
  Rejected: EWarrantStatus.Rejected,
};

/**
 * The same table as a value/label list: the label is what the Status column offers in its
 * filter, and the value is the AUTHSTATUS the backend has to be sent to filter on it. The
 * grid filters server side, so the two have to be told apart — see adaptToFilterModel on
 * the list container.
 *
 * Because the table above is many to one, a label can appear more than once — filtering on
 * Pending has to send every AUTHSTATUS that reads as Pending, which is why
 * adaptToServerValueFilterModel collects all the pairs a label matches rather than the first.
 */
export const eWarrantStatusValueLabels: ValueLabelPair[] =
  Object.entries(AUTH_STATUS_TO_EWARRANT_STATUS).map(([authStatus, status]) => ({
    value: authStatus,
    label: status,
  }));

export const adaptAuthStatus = (authStatus: string): EWarrantStatus =>
  // The cast keeps an unmapped Xcipio status visible in the grid (the status cell
  // renderer just drops its colour class) instead of mislabelling it as Pending.
  AUTH_STATUS_TO_EWARRANT_STATUS[authStatus] ?? (authStatus as EWarrantStatus);

/**
 * Widens a `GET /e-warrants` row into the `EWarrant` the store holds. The list read
 * only carries the four table columns, so everything the detail view needs — the
 * documents, the services, the approval block — comes back empty here and has to be
 * filled by a detail read before that view can be driven off real data.
 */
export const adaptEWarrantListItemToEWarrant = (item: EWarrantListItem): EWarrant => ({
  id: item.hi1ObjectId,
  status: adaptAuthStatus(item.status),
  rawStatus: item.status,
  interceptStatus: item.interceptStatus,
  decidedAt: null,
  decidedBy: null,
  decisionNote: '',
  authorisation: {
    HI1OBJECTID: item.hi1ObjectId,
    SENDERID: item.senderId,
    name: item.warrant,
    reference: item.warrant,
    sourceAgency: item.requestingAgency ?? '',
    receivedAt: item.received,
    description: '',
    priority: '',
    sourceWarrantId: '',
    receivingCsp: '',
    legalType: '',
    typeOfCase: '',
    desiredStatus: item.status,
    startTime: '',
    endTime: '',
    creationTimestamp: '',
    jurisdiction: '',
    legalEntity: '',
    cspId: '',
    countryCode: '',
    flags: [],
    approval: {
      type: '',
      reference: item.warrant,
      approverName: '',
      approverRole: '',
      approverEmail: '',
      approverPhone: '',
      timestamp: '',
      isEmergency: false,
    },
    manualInformation: '',
  },
  documents: [],
  services: [],
});

export const adaptEWarrantListResponse = (items: EWarrantListItem[] | null): EWarrant[] =>
  (items ?? []).map(adaptEWarrantListItemToEWarrant);

/**
 * A row the grid selected, in the shape the list actions and the action menu read it by.
 * The grid renders the rows the backend returned, so what is selected is a raw list item;
 * the fields no list read carries are filled the way the grid adapter fills them.
 */
export const adaptEWarrantListItemToGridRowData = (item: EWarrantListItem): EWarrantGridRowData => ({
  id: item.hi1ObjectId,
  senderId: item.senderId,
  name: item.warrant,
  sourceAgency: item.requestingAgency || NOT_AVAILABLE,
  receivingCsp: NOT_AVAILABLE,
  target: NOT_AVAILABLE,
  serviceTypes: NOT_AVAILABLE,
  documentCount: 0,
  receivedAt: item.received,
  status: adaptAuthStatus(item.status),
  interceptStatus: item.interceptStatus || NOT_AVAILABLE,
});

export const adaptEWarrantListItemsToGridRowData = (items: EWarrantListItem[]): EWarrantGridRowData[] =>
  items.map(adaptEWarrantListItemToGridRowData);

/**
 * DOCSTATUS uses the same vocabulary as AUTHSTATUS, so the same table reads it. A document
 * Xcipio holds no status for is undecided rather than unmapped — the column is not written
 * by every deployment, and an empty status shown verbatim would render as a blank badge.
 */
export const adaptDocumentStatus = (docStatus: string): EWarrantStatus =>
  (docStatus ? adaptAuthStatus(docStatus) : EWarrantStatus.Pending);

export const adaptEWarrantDocument = (document: EWarrantRawDocument): EWarrantDocument => ({
  ...document,
  docStatus: adaptDocumentStatus(document.docStatus),
});

/**
 * A detail read, ready to be merged onto the e-warrant the list produced. Only `status`
 * needs translating — the backend already emits every other field under the name the UI
 * reads it by. The array defaults matter: the view takes `.length` of both for its tab
 * titles. `interceptStatus` falls back to '' since Xcipio can leave the column unset,
 * keeping the store's `EWarrant.interceptStatus: string` contract honest.
 */
export const adaptEWarrantDetailResponse = (detail: EWarrantDetailResponse): EWarrantDetailPatch => ({
  ...detail,
  status: adaptAuthStatus(detail.status),
  rawStatus: detail.status,
  interceptStatus: detail.interceptStatus ?? '',
  documents: (detail.documents ?? []).map(adaptEWarrantDocument),
  services: detail.services ?? [],
});

/*
 * There is deliberately no "full e-warrant to grid row" adapter: the grid reads its rows
 * from the server, so the row it hands back is the list item the backend returned, and
 * adaptEWarrantListItemToGridRowData above is what turns that into the summary the list
 * actions and the action menu read.
 */
